import { Fragment, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../../lib/api'
import { Card } from '../../components/ui/Card'
import { Pagination, type PageSize } from '../../components/ui/Pagination'
import { DateQuickButtons, DateRangeField, currentYearRange } from '../../components/ui/DateField'
import { ExportDropdown } from '../../components/ui/ExportDropdown'
import { downloadXlsx } from '../../lib/downloadXlsx'
import { useAuthStore } from '../../stores/auth'

interface EmagOrderProduct {
  productId: string
  productName: string | null
  partNumber: string | null
  partNumberKey: string | null
  quantity: number
  unitPrice: string
  vatRate: string
}

interface RecurrenceBucket {
  key: string
  label: string
  min: number
  max: number | null
  count: number
}

interface EmagOrdersStats {
  total: number
  recurring: number
  recurringClients: number
  recurringPct: number
  card: number
  cardPct: number
  courier: number
  courierPct: number
  genius: number
  geniusClients: number
  geniusPct: number
  locker: number
  lockerPct: number
  buckets: RecurrenceBucket[]
}

const RECURRENCE_BUCKETS = [
  { key: '1', label: '1', min: 1, max: 1 as number | null },
  { key: '2-5', label: '2–5', min: 2, max: 5 as number | null },
  { key: '6-10', label: '6–10', min: 6, max: 10 as number | null },
  { key: '11-15', label: '11–15', min: 11, max: 15 as number | null },
  { key: '16-20', label: '16–20', min: 16, max: 20 as number | null },
  { key: '21+', label: '21+', min: 21, max: null as number | null },
]

interface EmagOrder {
  id: number
  emagOrderId: string
  status: number
  date: string
  paymentMode: string | null
  customerName: string | null
  customerCif: string | null
  customerEmail: string | null
  customerPhone: string | null
  totalWithVat: string | null
  awbNumber: string | null
  warehouseId: number | null
  isStorno: boolean
  isGenius: boolean
  isLocker: boolean
  products: EmagOrderProduct[]
  clientOrderCount: number
  clientOrderTotal: string
}

interface EmagClient {
  clientKey: string
  customerName: string | null
  phone: string | null
  customerCif: string | null
  orderCount: number
  totalValue: string
  firstDate: string
  lastDate: string
  isGenius: boolean
  isLocker: boolean
}

const STATUS_LABEL: Record<number, string> = {
  0: 'Anulat',
  1: 'Nou',
  2: 'În lucru',
  3: 'Pregătită',
  4: 'Finalizată',
  5: 'Returnată',
}

const STATUS_COLOR: Record<number, string> = {
  0: 'text-b4l-text-muted',
  1: 'text-b4l-blue-dark',
  2: 'text-b4l-amber-dark',
  3: 'text-b4l-amber-dark',
  4: 'text-b4l-green-dark',
  5: 'text-b4l-red-dark',
}

// Comenzile unui singur client (sub-tabel la expand in modul Grupeaza pe client).
function ClientOrders({ urlParams }: { urlParams: string }) {
  const { data, isLoading } = useQuery<{ orders: EmagOrder[]; total: number }>({
    queryKey: ['emag-client-orders', urlParams],
    queryFn: async () => (await api.get(`/emag/orders?${urlParams}`)).data,
  })
  if (isLoading) return <p className="text-[11px] text-b4l-text-muted">Se încarcă comenzile…</p>
  const orders = data?.orders ?? []
  if (orders.length === 0) return <p className="text-[11px] text-b4l-text-muted">Nicio comandă.</p>
  return (
    <table className="w-full text-[11px]">
      <thead className="text-left text-b4l-text-muted">
        <tr>
          <th className="py-1 pr-3">ID eMAG</th>
          <th className="py-1 pr-3">Data</th>
          <th className="py-1 pr-3">Status</th>
          <th className="py-1 pr-3 text-right">Total</th>
          <th className="py-1 pr-3">Produse</th>
          <th className="py-1 pr-3">Plată</th>
        </tr>
      </thead>
      <tbody>
        {orders.map((o) => (
          <tr key={o.id} className="border-t border-b4l-border/50">
            <td className="py-1 pr-3 font-mono">{o.emagOrderId}</td>
            <td className="py-1 pr-3">{new Date(o.date).toLocaleDateString('en-GB')}</td>
            <td className={`py-1 pr-3 ${STATUS_COLOR[o.status] ?? ''}`}>
              {STATUS_LABEL[o.status] ?? o.status}
              {o.isStorno && (
                <span className="ml-1 rounded bg-b4l-red-light px-1 text-b4l-red-dark">STORNO</span>
              )}
            </td>
            <td className="py-1 pr-3 text-right">
              {o.totalWithVat ? Number(o.totalWithVat).toFixed(2) : '—'}
            </td>
            <td className="py-1 pr-3">
              {o.products.length} buc / {o.products.reduce((a, p) => a + p.quantity, 0)} unit
            </td>
            <td className="py-1 pr-3">{o.paymentMode ?? '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export function ComenziEmagPage() {
  /** Exportul e un drept PERSONAL: nu vine cu niciun rol, nici cu `admin`. */
  const poateExporta = useAuthStore((s) => s.permissions).includes('date.clienti:export')
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState<PageSize>(50)
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [client, setClient] = useState('')
  const [orderNo, setOrderNo] = useState('')
  const [dateFrom, setDateFrom] = useState(currentYearRange().from)
  const [dateTo, setDateTo] = useState(currentYearRange().to)
  const [product, setProduct] = useState('')
  const [genius, setGenius] = useState('') // '' | '1' | '0'
  const [locker, setLocker] = useState('') // '' | '1' | '0'
  const [recurrenceBucket, setRecurrenceBucket] = useState('')
  const [expanded, setExpanded] = useState<Set<number>>(new Set())
  const [groupByClient, setGroupByClient] = useState(true)
  const [expandedClients, setExpandedClients] = useState<Set<string>>(new Set())
  const toggleClient = (k: string) =>
    setExpandedClients((prev) => {
      const next = new Set(prev)
      if (next.has(k)) next.delete(k)
      else next.add(k)
      return next
    })

  const toggleExpand = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const buildParams = (withPaging: boolean) => {
    const params = new URLSearchParams()
    if (withPaging) {
      params.set('limit', String(pageSize))
      params.set('offset', String(page * pageSize))
    }
    if (statusFilter) params.set('status', statusFilter)
    if (client.trim()) params.set('client', client.trim())
    if (orderNo.trim()) params.set('orderNo', orderNo.trim())
    if (product.trim()) params.set('product', product.trim())
    if (genius) params.set('genius', genius)
    if (locker) params.set('locker', locker)
    if (dateFrom) params.set('dateFrom', dateFrom)
    if (dateTo) params.set('dateTo', dateTo)
    return params
  }

  const { data, isLoading } = useQuery<{ orders: EmagOrder[]; total: number }>({
    queryKey: [
      'emag-orders',
      page,
      pageSize,
      statusFilter,
      client,
      orderNo,
      product,
      genius,
      locker,
      dateFrom,
      dateTo,
      recurrenceBucket,
    ],
    enabled: !groupByClient,
    queryFn: async () => {
      const params = buildParams(true)
      const b = RECURRENCE_BUCKETS.find((x) => x.key === recurrenceBucket)
      if (b) {
        params.set('minOrders', String(b.min))
        if (b.max != null) params.set('maxOrders', String(b.max))
      }
      return (await api.get(`/emag/orders?${params}`)).data
    },
  })

  // Params pt comenzile unui singur client (la expand in modul grupat).
  const clientOrdersParams = (clientKey: string) => {
    const params = buildParams(false)
    const b = RECURRENCE_BUCKETS.find((x) => x.key === recurrenceBucket)
    if (b) {
      params.set('minOrders', String(b.min))
      if (b.max != null) params.set('maxOrders', String(b.max))
    }
    params.set('clientKey', clientKey)
    params.set('limit', '500')
    return params.toString()
  }

  // Comenzi grupate pe client (cand e activat toggle-ul Grupeaza pe client).
  const { data: clientsData, isLoading: clientsLoading } = useQuery<{
    clients: EmagClient[]
    total: number
    recurringTotal: number
  }>({
    queryKey: [
      'emag-orders-by-client',
      page,
      pageSize,
      statusFilter,
      client,
      orderNo,
      product,
      genius,
      locker,
      dateFrom,
      dateTo,
      recurrenceBucket,
    ],
    enabled: groupByClient,
    queryFn: async () => {
      const params = buildParams(true)
      const b = RECURRENCE_BUCKETS.find((x) => x.key === recurrenceBucket)
      if (b) {
        params.set('minOrders', String(b.min))
        if (b.max != null) params.set('maxOrders', String(b.max))
      }
      return (await api.get(`/emag/orders/by-client?${params}`)).data
    },
  })

  // Statistici (nu depind de paginare sau de segmentul de recurenta selectat).
  const { data: stats } = useQuery<EmagOrdersStats>({
    queryKey: [
      'emag-orders-stats',
      statusFilter,
      client,
      orderNo,
      product,
      genius,
      locker,
      dateFrom,
      dateTo,
    ],
    queryFn: async () => (await api.get(`/emag/orders/stats?${buildParams(false)}`)).data,
  })

  const selectBucket = (key: string) => {
    setRecurrenceBucket((prev) => (prev === key ? '' : key))
    setPage(0)
  }

  const [exporting, setExporting] = useState(false)
  const exportOrders = async (currentPageOnly: boolean) => {
    try {
      setExporting(true)
      const params = buildParams(currentPageOnly)
      const b = RECURRENCE_BUCKETS.find((x) => x.key === recurrenceBucket)
      if (b) {
        params.set('minOrders', String(b.min))
        if (b.max != null) params.set('maxOrders', String(b.max))
      }
      await downloadXlsx(`/emag/orders/export?${params.toString()}`)
      toast.success('Export descărcat')
    } catch {
      toast.error('Eroare la export')
    } finally {
      setExporting(false)
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">eMAG → Comenzi</h1>
        {/*
          Butonul dispare pentru cine n-are dreptul. Nu e protectie — aceea e pe ruta, care refuza
          oricum — ci politete: sa nu apesi ceva ce vine inapoi cu „interzis".
        */}
        {poateExporta && (
          <ExportDropdown
            loading={exporting}
            pageCount={data?.orders?.length}
            totalCount={data?.total}
            onExportPage={() => exportOrders(true)}
            onExportAll={() => exportOrders(false)}
          />
        )}
      </div>

      <Card>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => {
              setStatusFilter('')
              setPage(0)
            }}
            className={`rounded border px-3 py-1 text-xs ${
              !statusFilter
                ? 'border-b4l-text bg-b4l-text text-white'
                : 'border-b4l-border bg-white'
            }`}
          >
            Toate
          </button>
          {Object.entries(STATUS_LABEL).map(([s, l]) => (
            <button
              key={s}
              onClick={() => {
                setStatusFilter(s)
                setPage(0)
              }}
              className={`rounded border px-3 py-1 text-xs ${
                statusFilter === s
                  ? 'border-b4l-text bg-b4l-text text-white'
                  : 'border-b4l-border bg-white'
              }`}
            >
              {l}
            </button>
          ))}
        </div>
      </Card>

      <Card title="Filtre">
        <DateQuickButtons
          fromValue={dateFrom}
          toValue={dateTo}
          onChange={(f, t) => {
            setDateFrom(f)
            setDateTo(t)
            setPage(0)
          }}
        />
        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div className="lg:col-span-2">
            <DateRangeField
              fromValue={dateFrom}
              toValue={dateTo}
              onChange={(f, t) => {
                setDateFrom(f)
                setDateTo(t)
                setPage(0)
              }}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-b4l-text-muted">Client</label>
            <input
              type="text"
              value={client}
              onChange={(e) => {
                setClient(e.target.value)
                setPage(0)
              }}
              placeholder="Nume client…"
              className="w-full rounded border border-b4l-border bg-white px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-b4l-text-muted">
              Nr. comandă
            </label>
            <input
              type="text"
              value={orderNo}
              onChange={(e) => {
                setOrderNo(e.target.value)
                setPage(0)
              }}
              placeholder="ID eMAG…"
              className="w-full rounded border border-b4l-border bg-white px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-b4l-text-muted">Produs</label>
            <input
              type="text"
              value={product}
              onChange={(e) => {
                setProduct(e.target.value)
                setPage(0)
              }}
              placeholder="Denumire / SKU / PNK…"
              className="w-full rounded border border-b4l-border bg-white px-3 py-2 text-sm"
            />
          </div>
        </div>
      </Card>

      {stats && (
        <Card title="Sinteză (după filtre)">
          <div className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-5">
            <div>
              <div className="text-xs text-b4l-text-muted">Total comenzi</div>
              <div className="text-lg font-semibold">{stats.total.toLocaleString('ro-RO')}</div>
            </div>
            <div>
              <div className="text-xs text-b4l-text-muted">Clienți recurenți</div>
              <div className="text-lg font-semibold text-b4l-green-dark">
                {stats.recurringClients.toLocaleString('ro-RO')}
                <span className="ml-1 text-xs font-normal text-b4l-text-muted">
                  clienți · {stats.recurringPct}% comenzi
                </span>
              </div>
            </div>
            <div>
              <div className="text-xs text-b4l-text-muted">Plată card</div>
              <div className="text-lg font-semibold">
                {stats.cardPct}%
                <span className="ml-1 text-xs font-normal text-b4l-text-muted">
                  ({stats.card.toLocaleString('ro-RO')})
                </span>
              </div>
            </div>
            <div>
              <div className="text-xs text-b4l-text-muted">Plată ramburs (curier)</div>
              <div className="text-lg font-semibold">
                {stats.courierPct}%
                <span className="ml-1 text-xs font-normal text-b4l-text-muted">
                  ({stats.courier.toLocaleString('ro-RO')})
                </span>
              </div>
            </div>
            <div>
              <div className="text-xs text-b4l-text-muted">Clienți Genius</div>
              <div className="text-lg font-semibold text-b4l-blue-dark">
                {stats.geniusClients.toLocaleString('ro-RO')}
                <span className="ml-1 text-xs font-normal text-b4l-text-muted">
                  clienți · {stats.geniusPct}% comenzi
                </span>
              </div>
            </div>
            <div>
              <div className="text-xs text-b4l-text-muted">Livrare locker</div>
              <div className="text-lg font-semibold">
                {stats.lockerPct}%
                <span className="ml-1 text-xs font-normal text-b4l-text-muted">
                  ({stats.locker.toLocaleString('ro-RO')})
                </span>
              </div>
            </div>
          </div>

          <div className="mt-4">
            <div className="mb-1.5 text-xs font-medium text-b4l-text-muted">Filtrare Genius</div>
            <div className="flex flex-wrap gap-2">
              {[
                { k: '', l: 'Toți' },
                { k: '1', l: 'Doar Genius' },
                { k: '0', l: 'Non-Genius' },
              ].map((g) => (
                <button
                  key={g.k}
                  onClick={() => {
                    setGenius(g.k)
                    setPage(0)
                  }}
                  className={`rounded border px-3 py-1 text-xs ${
                    genius === g.k
                      ? 'border-b4l-text bg-b4l-text text-white'
                      : 'border-b4l-border bg-white'
                  }`}
                >
                  {g.l}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4">
            <div className="mb-1.5 text-xs font-medium text-b4l-text-muted">Filtrare livrare</div>
            <div className="flex flex-wrap gap-2">
              {[
                { k: '', l: 'Toate' },
                { k: '1', l: 'Doar locker' },
                { k: '0', l: 'Curier' },
              ].map((g) => (
                <button
                  key={g.k}
                  onClick={() => {
                    setLocker(g.k)
                    setPage(0)
                  }}
                  className={`rounded border px-3 py-1 text-xs ${
                    locker === g.k
                      ? 'border-b4l-text bg-b4l-text text-white'
                      : 'border-b4l-border bg-white'
                  }`}
                >
                  {g.l}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4">
            <div className="mb-1.5 text-xs font-medium text-b4l-text-muted">
              Segmentare după revenire — nr. de clienți pe câte comenzi au (click pentru filtrare)
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => selectBucket('')}
                className={`rounded border px-3 py-1 text-xs ${
                  !recurrenceBucket
                    ? 'border-b4l-text bg-b4l-text text-white'
                    : 'border-b4l-border bg-white'
                }`}
              >
                Toți
              </button>
              {stats.buckets.map((b) => (
                <button
                  key={b.key}
                  onClick={() => selectBucket(b.key)}
                  className={`rounded border px-3 py-1 text-xs ${
                    recurrenceBucket === b.key
                      ? 'border-b4l-text bg-b4l-text text-white'
                      : 'border-b4l-border bg-white'
                  }`}
                >
                  {b.label}
                  <span
                    className={`ml-1 ${recurrenceBucket === b.key ? 'text-white/70' : 'text-b4l-text-muted'}`}
                  >
                    ({b.count.toLocaleString('ro-RO')})
                  </span>
                </button>
              ))}
            </div>
          </div>
        </Card>
      )}

      <Card>
        <div className="mb-2 flex items-center justify-between">
          <label className="flex cursor-pointer items-center gap-2 text-xs font-medium text-b4l-text">
            <input
              type="checkbox"
              checked={groupByClient}
              onChange={(e) => {
                setGroupByClient(e.target.checked)
                setPage(0)
              }}
            />
            Grupează pe client
          </label>
          {groupByClient && clientsData && (
            <span className="text-xs text-b4l-text-muted">
              {clientsData.total.toLocaleString('ro-RO')} clienți ·{' '}
              <span className="font-semibold text-b4l-text">
                {clientsData.recurringTotal.toLocaleString('ro-RO')} recurenți
              </span>{' '}
              (&gt;1 comandă)
            </span>
          )}
        </div>

        {groupByClient && (
          <>
            {clientsLoading && <p className="text-sm text-b4l-text-muted">Se încarcă…</p>}
            {clientsData && clientsData.clients.length === 0 && (
              <p className="text-sm text-b4l-text-muted">Niciun client găsit.</p>
            )}
            {clientsData && clientsData.clients.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="border-b border-b4l-border text-left text-b4l-text-muted">
                    <tr>
                      <th className="py-2 pr-3">Client</th>
                      <th className="py-2 pr-3">Telefon</th>
                      <th className="py-2 pr-3">CIF</th>
                      <th className="py-2 pr-3 text-right">Comenzi</th>
                      <th className="py-2 pr-3 text-right">Total client</th>
                      <th className="py-2 pr-3">Perioadă</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientsData.clients.map((c) => (
                      <Fragment key={c.clientKey}>
                        <tr
                          onClick={() => toggleClient(c.clientKey)}
                          className="cursor-pointer border-b border-b4l-border hover:bg-b4l-bg-soft"
                        >
                          <td className="py-2 pr-3">
                            <span className="mr-1 inline-block text-b4l-text-muted">
                              {expandedClients.has(c.clientKey) ? '▾' : '▸'}
                            </span>
                            {c.customerName ?? '—'}
                            {c.isGenius && (
                              <span className="ml-1 rounded bg-b4l-blue-light px-1 text-[10px] font-medium text-b4l-blue-dark">
                                Genius
                              </span>
                            )}
                            {c.isLocker && (
                              <span className="ml-1 rounded bg-b4l-bg-soft px-1 text-[10px] font-medium text-b4l-text-muted">
                                Locker
                              </span>
                            )}
                          </td>
                          <td className="py-2 pr-3 font-mono">{c.phone ?? '—'}</td>
                          <td className="py-2 pr-3 font-mono">{c.customerCif ?? '—'}</td>
                          <td className="py-2 pr-3 text-right">
                            {c.orderCount > 1 ? (
                              <span className="rounded bg-b4l-green-light px-1.5 py-0.5 font-medium text-b4l-green-dark">
                                {c.orderCount}× recurent
                              </span>
                            ) : (
                              <span className="text-b4l-text-muted">1</span>
                            )}
                          </td>
                          <td className="py-2 pr-3 text-right font-semibold">
                            {Number(c.totalValue).toFixed(2)}
                          </td>
                          <td className="py-2 pr-3 text-b4l-text-muted">
                            {new Date(c.firstDate).toLocaleDateString('en-GB')} –{' '}
                            {new Date(c.lastDate).toLocaleDateString('en-GB')}
                          </td>
                        </tr>
                        {expandedClients.has(c.clientKey) && (
                          <tr className="border-b border-b4l-border bg-b4l-bg-soft">
                            <td colSpan={6} className="px-3 py-2">
                              <ClientOrders urlParams={clientOrdersParams(c.clientKey)} />
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {!groupByClient && isLoading && <p className="text-sm text-b4l-text-muted">Se încarcă…</p>}
        {!groupByClient && data?.orders?.length === 0 && (
          <p className="text-sm text-b4l-text-muted">Nicio comandă găsită.</p>
        )}
        {!groupByClient && data?.orders && data.orders.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="border-b border-b4l-border text-left text-b4l-text-muted">
                <tr>
                  <th className="py-2 pr-3">ID eMAG</th>
                  <th className="py-2 pr-3">Data</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Client</th>
                  <th className="py-2 pr-3">Telefon</th>
                  <th className="py-2 pr-3 text-right">Comenzi client</th>
                  <th className="py-2 pr-3 text-right">Total client</th>
                  <th className="py-2 pr-3">CIF</th>
                  <th className="py-2 pr-3">Total</th>
                  <th className="py-2 pr-3">Produse</th>
                  <th className="py-2 pr-3">Plată</th>
                </tr>
              </thead>
              <tbody>
                {data.orders.map((o) => (
                  <Fragment key={o.id}>
                    <tr
                      onClick={() => toggleExpand(o.id)}
                      className="cursor-pointer border-b border-b4l-border hover:bg-b4l-bg-soft"
                    >
                      <td className="py-2 pr-3 font-mono">{o.emagOrderId}</td>
                      <td className="py-2 pr-3">{new Date(o.date).toLocaleDateString('en-GB')}</td>
                      <td className={`py-2 pr-3 ${STATUS_COLOR[o.status] ?? ''}`}>
                        {STATUS_LABEL[o.status] ?? o.status}
                        {o.isStorno && (
                          <span className="ml-1 rounded bg-b4l-red-light px-1 text-b4l-red-dark">
                            STORNO
                          </span>
                        )}
                      </td>
                      <td className="py-2 pr-3">
                        {o.customerName ?? '—'}
                        {o.isGenius && (
                          <span className="ml-1 rounded bg-b4l-blue-light px-1 text-[10px] font-medium text-b4l-blue-dark">
                            Genius
                          </span>
                        )}
                        {o.isLocker && (
                          <span className="ml-1 rounded bg-b4l-bg-soft px-1 text-[10px] font-medium text-b4l-text-muted">
                            Locker
                          </span>
                        )}
                      </td>
                      <td className="py-2 pr-3 font-mono">{o.customerPhone ?? '—'}</td>
                      <td className="py-2 pr-3 text-right">
                        {o.clientOrderCount > 1 ? (
                          <span className="rounded bg-b4l-green-light px-1.5 py-0.5 font-medium text-b4l-green-dark">
                            {o.clientOrderCount}× recurent
                          </span>
                        ) : (
                          <span className="text-b4l-text-muted">1</span>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-right">
                        {Number(o.clientOrderTotal).toFixed(2)}
                      </td>
                      <td className="py-2 pr-3 font-mono">{o.customerCif ?? '—'}</td>
                      <td className="py-2 pr-3 text-right">
                        {o.totalWithVat ? Number(o.totalWithVat).toFixed(2) : '—'}
                      </td>
                      <td className="py-2 pr-3">
                        <span className="mr-1 inline-block text-b4l-text-muted">
                          {expanded.has(o.id) ? '▾' : '▸'}
                        </span>
                        {o.products.length} buc / {o.products.reduce((a, p) => a + p.quantity, 0)}{' '}
                        unit
                      </td>
                      <td className="py-2 pr-3">{o.paymentMode ?? '—'}</td>
                    </tr>
                    {expanded.has(o.id) && (
                      <tr className="border-b border-b4l-border bg-b4l-bg-soft">
                        <td colSpan={11} className="px-3 py-2">
                          {o.products.length === 0 ? (
                            <p className="text-b4l-text-muted">Fără produse.</p>
                          ) : (
                            <table className="w-full text-[11px]">
                              <thead className="text-left text-b4l-text-muted">
                                <tr>
                                  <th className="py-1 pr-3">Denumire</th>
                                  <th className="py-1 pr-3">SKU</th>
                                  <th className="py-1 pr-3">PNK</th>
                                  <th className="py-1 pr-3 text-right">Cant.</th>
                                  <th className="py-1 pr-3 text-right">Preț (fără TVA)</th>
                                  <th className="py-1 pr-3 text-right">TVA</th>
                                  <th className="py-1 pr-3 text-right">Total linie (cu TVA)</th>
                                </tr>
                              </thead>
                              <tbody>
                                {o.products.map((p) => {
                                  const unit = Number(p.unitPrice) || 0
                                  const vat = Number(p.vatRate) || 0
                                  const vatPct = vat > 1 ? vat : vat * 100
                                  const lineTotal =
                                    p.quantity * unit * (vat > 1 ? 1 + vat / 100 : 1 + vat)
                                  return (
                                    <tr key={p.productId + (p.partNumberKey ?? '')}>
                                      <td className="py-1 pr-3">{p.productName ?? '—'}</td>
                                      <td className="py-1 pr-3 font-mono">{p.partNumber ?? '—'}</td>
                                      <td className="py-1 pr-3 font-mono">
                                        {p.partNumberKey ?? '—'}
                                      </td>
                                      <td className="py-1 pr-3 text-right">{p.quantity}</td>
                                      <td className="py-1 pr-3 text-right">{unit.toFixed(2)}</td>
                                      <td className="py-1 pr-3 text-right">{vatPct.toFixed(0)}%</td>
                                      <td className="py-1 pr-3 text-right">
                                        {lineTotal.toFixed(2)}
                                      </td>
                                    </tr>
                                  )
                                })}
                              </tbody>
                            </table>
                          )}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {(groupByClient ? clientsData?.total : data?.total) != null && (
          <div className="mt-3">
            <Pagination
              page={page}
              pageSize={pageSize}
              total={(groupByClient ? clientsData?.total : data?.total) ?? 0}
              onPageChange={setPage}
              onPageSizeChange={(s) => {
                setPageSize(s)
                setPage(0)
              }}
              itemLabel={groupByClient ? 'clienți' : 'comenzi'}
            />
          </div>
        )}
      </Card>
    </div>
  )
}
